import { Check, X } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { BookingBand } from "@/components/site/BookingBand";
import { Sparkle } from "@/components/site/Sparkle";
import { IMG, LASER_SERVICES, BOOKING_URL } from "@/data/site";

const MenuRow = ({ item, index }) => (
  <Reveal delay={0.05 * index}>
    <div
      data-testid={`laser-menu-item-${index}`}
      className="group grid grid-cols-1 sm:grid-cols-12 gap-4 sm:gap-6 items-center py-6 border-b border-stone hover:border-gold transition-colors duration-500"
    >
      <div className="sm:col-span-6 flex items-center gap-4 sm:gap-5">
        <span className="hidden md:block font-display text-lg text-champagne w-7 shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>
        <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 overflow-hidden border border-stone group-hover:border-gold transition-colors duration-500">
          <img
            src={item.img}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          />
        </div>
        <h3 className="font-display text-2xl sm:text-3xl text-espresso group-hover:text-gold group-hover:translate-x-1.5 transition-all duration-500">
          {item.name}
        </h3>
      </div>
      <p className="sm:col-span-4 text-deep/70 text-sm leading-relaxed">{item.desc}</p>
      <div className="sm:col-span-2 sm:text-right">
        <span className="font-display text-xl text-gold whitespace-nowrap">{item.duration}</span>
      </div>
    </div>
  </Reveal>
);

const DOS = [
  "Arrive with clean, product-free skin",
  "Shave the treatment area 24 hours before laser",
  "Wear SPF daily before and after your course",
  "Tell us about medications and recent sun exposure",
];
const DONTS = [
  "No waxing, threading, or plucking between sessions",
  "Avoid active tanning for two weeks prior",
  "Skip retinoids and acids 48 hours around treatment",
  "No hot saunas or intense workouts for 24 hours after",
];

export default function ServicesLaser() {
  return (
    <>
      <PageHero
        testId="services-laser-hero"
        eyebrow="Services — Laser & Skin"
        titleLines={["Advanced.", "Gentle. Proven."]}
        quote="Medical-grade technology in a calm, private setting — designed around your skin."
        image={IMG.laser1}
        imageAlt="A clinical laser and skin treatment in a private S & Z suite"
      />

      <section data-testid="laser-menu" className="bg-ivory py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal className="mb-12">
            <Eyebrow>The Skin & Laser Menu</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-espresso mt-4">
              Technology, <span className="italic text-gold">Tempered With Care</span>
            </h2>
            <p className="text-deep/60 text-sm mt-5 max-w-xl">
              Every course begins with a consultation and patch test. Timings are estimates, confirmed at your consultation.
            </p>
          </Reveal>
          <div>
            {LASER_SERVICES.map((s, i) => (
              <MenuRow key={s.name} item={s} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section data-testid="laser-technology" className="bg-espresso py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-5">
              <img src={IMG.spa3} alt="Calm clinical facial treatment" loading="lazy" className="w-full h-64 sm:h-80 object-cover border border-ivory/15 img-frame" />
              <img src={IMG.laser3} alt="Skin treatment in a private suite" loading="lazy" className="w-full h-64 sm:h-80 object-cover border border-ivory/15 img-frame mt-10" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <Eyebrow light>The Technology</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-ivory mt-4 leading-tight">
              Medical-Grade, <span className="italic text-gold">Never One-Size-Fits-All</span>
            </h2>
            <p className="text-ivory/65 text-base sm:text-lg mt-6 leading-relaxed">
              Settings, wavelengths, and course length are calibrated to your
              skin tone, hair type, and history — reviewed at every visit, never
              set and forgotten.
            </p>
            <ul className="mt-8 space-y-3">
              {["Safe across a wide range of skin tones", "Private suites — never an open treatment floor", "Honest guidance, including when to say no"].map((t) => (
                <li key={t} className="flex items-center gap-3 font-sans uppercase tracking-[0.16em] text-[11px] text-champagne/90">
                  <Sparkle size={9} /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section data-testid="laser-care-protocol" className="bg-ivory py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal className="text-center mb-14">
            <Eyebrow>
              <span className="mx-auto">The Protocol</span>
            </Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl text-espresso mt-4">
              Pre &amp; Post <span className="italic text-gold">Care</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Reveal>
              <div className="border border-stone bg-ivory p-10 img-frame h-full">
                <h3 className="font-display text-2xl text-espresso mb-8">Before &amp; After — Do</h3>
                <ul className="space-y-5">
                  {DOS.map((t) => (
                    <li key={t} className="flex items-start gap-4 text-deep/80 text-sm leading-relaxed">
                      <Check size={16} strokeWidth={1.5} className="text-gold mt-0.5 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="border border-stone bg-ivory p-10 img-frame h-full">
                <h3 className="font-display text-2xl text-espresso mb-8">Before &amp; After — Don't</h3>
                <ul className="space-y-5">
                  {DONTS.map((t) => (
                    <li key={t} className="flex items-start gap-4 text-deep/80 text-sm leading-relaxed">
                      <X size={16} strokeWidth={1.5} className="text-gold mt-0.5 shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal className="text-center mt-14">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="laser-consultation-cta"
              aria-label="Book a consultation (opens in a new tab)"
              className="inline-block font-sans uppercase tracking-[0.2em] text-xs font-medium px-8 py-4 border border-gold text-gold hover:bg-gold hover:text-espresso transition-all duration-300"
            >
              Book a Skin Consultation
            </a>
          </Reveal>
        </div>
      </section>

      <BookingBand />
    </>
  );
}
